/**
 * useShellReads — the shell's server read-back and everything derived from it:
 * the Decisions, Action List and Daily Logs reads, whether the project refused
 * this person, the unseen write rejections, the project's local log records,
 * and the server log rows adapted to the record shape the log list renders.
 *
 * `discardedLogIds` stays with the shell, which adds to it when a discard lands;
 * this hook only hides those rows.
 */

import { useMemo } from "preact/hooks";
import type { MasterItem } from "../components/MasterActionListView";
import { payloadStr } from "../components/entry-meta";
import { SEED_DECISIONS, SEED_MASTER_ITEMS } from "../dev/seed-fixtures";
import { acknowledgeRejection } from "../outbox/state-machine";
import type { OutboxRecord } from "../outbox/types";
import type { LogEntry, ReadClient } from "../read/read-client";
import {
  reconcileActions,
  reconcileDecisions,
  serverLogEntryAsRecord,
  serverOnlyLogEntries,
} from "../read/reconcile";
import type { Decision } from "../slice/decision-slice";
import { useReadBack } from "./use-read-back";

export interface ShellReadsDeps {
  project_ref: string | null;
  /** `null` when no endpoint was baked in; the tabs then fall back to the dev fixtures. */
  readClient: ReadClient | null;
  tenant_id: string;
  skill_version: string;
  outboxRecords: OutboxRecord[];
  refreshOutbox: () => Promise<void>;
  /** Log entries discarded on the server, hidden before the next read lands. */
  discardedLogIds: Set<string>;
}

export function useShellReads({
  project_ref,
  readClient,
  tenant_id,
  skill_version,
  outboxRecords,
  refreshOutbox,
  discardedLogIds,
}: ShellReadsDeps) {
  const decisionsRead = useReadBack<Decision>({
    surface: "decisions",
    project_ref,
    client: readClient,
    // Open-only: the Decision Log is the walk-of-shame of what still needs
    // deciding (AC-1f-13). Resolved decisions are not padded into it.
    fetcher: (c: ReadClient) => c.fetchDecisions({ project_ref: project_ref ?? "" }),
    records: outboxRecords,
    reconcile: reconcileDecisions,
  });
  const actionsRead = useReadBack<MasterItem>({
    surface: "actions",
    project_ref,
    client: readClient,
    fetcher: (c: ReadClient) => c.fetchActions({ project_ref: project_ref ?? "" }),
    records: outboxRecords,
    reconcile: reconcileActions,
  });
  // The daily log's server read. Unlike Decisions and the Action List — which
  // render FROM the server view — this BACKFILLS: the log renders from this
  // device's own outbox (which holds the photo blobs and the pre-delivery
  // states), and the server supplies only the entries this device has no record
  // of. That is the factory-reset / second-phone / other-principal case, where
  // the log would otherwise be empty while the server plainly holds it.
  const logsRead = useReadBack<LogEntry>({
    surface: "logs",
    project_ref,
    client: readClient,
    fetcher: (c: ReadClient) => c.fetchLogs({ project_ref: project_ref ?? "" }),
    records: outboxRecords,
    reconcile: serverOnlyLogEntries,
  });
  // Being REFUSED a project is not the same as having nothing in it, and until
  // now they looked identical: `use-read-back` correctly clears the rows on
  // `project_out_of_scope` (showing another project's data under this one's name
  // is the worse failure), but nobody consumed the error, so the person just saw
  // an empty app and no reason for it.
  //
  // Any ONE surface is enough to know: scope is per-PROJECT, so a refusal
  // refuses all of them. `invalid_token` is deliberately excluded — that is a
  // sign-in problem, not an access one, and telling someone they lack access to
  // a project when their code has simply lapsed would send them to the wrong
  // person for help.
  const projectRefused =
    logsRead.error === "project_out_of_scope" ||
    decisionsRead.error === "project_out_of_scope" ||
    actionsRead.error === "project_out_of_scope";

  /** Writes this device sent that the server REFUSED, and nobody has been told
   * about yet.
   *
   * Scoped to the selected project so a refusal on Fray does not nag someone
   * working on Foster — the same reason Daily Logs is scoped. Unscoped records
   * (a refusal that never carried a project_ref) are shown regardless: hiding one
   * because we cannot place it would be the silence this exists to end. */
  const unseenRejections = outboxRecords.filter(
    (r) =>
      r.state === "rejected" &&
      !r.rejectionSeenAt &&
      (project_ref === null ||
        r.envelope.payload["project_ref"] === undefined ||
        r.envelope.payload["project_ref"] === project_ref),
  );

  async function dismissRejection(capture_id: string) {
    await acknowledgeRejection(capture_id);
    await refreshOutbox();
  }
  // Daily Logs is the ONLY view that renders DEVICE-LOCAL records, and it was
  // handed the entire device store — so every capture appeared under every
  // project. Red, field test 2026-07-29: "The data is bleeding between the two
  // projects for the daily logs, but not action list, decision list or the
  // reports tabs." Those three read from the server, which scopes by
  // `project_ref`, which is exactly why they were clean; the server data was
  // never wrong.
  //
  // Filter STRICTLY. A record carrying no `project_ref` cannot be attributed to
  // any project, and rendering it under all of them IS the bug. `payloadStr` and
  // not `inheritedProjectRef`: that helper THROWS on an unscoped record, because
  // its job is to refuse to emit one — it is not a predicate.
  //
  // Only this list is filtered. The read-back reconcilers above want the whole
  // device (they answer "is this server row already local?", and capture_ids are
  // unique across projects), and FeedbackTab's debug bundle is device-wide on
  // purpose.
  const projectLocalRecords = useMemo(
    () =>
      project_ref === null
        ? []
        : outboxRecords.filter((r) => payloadStr(r, "project_ref") === project_ref),
    [outboxRecords, project_ref],
  );

  // Adapted to the row shape the list already renders. READ-ONLY: these carry no
  // local blobs and no client_id, so they are never given a tap target and must
  // never reach submitCorrection/submitRemoval/deleteLocalRecord.
  const serverOnlyLogRows = useMemo(
    () =>
      project_ref === null
        ? []
        : logsRead.items.filter((e) => !discardedLogIds.has(e.id)).map((e) =>
            // #23a: the scope the READ was made under — not a re-selection. The
            // logs read is already project-scoped, so this is the same project
            // the rows came from, and a correction inherits it verbatim.
            serverLogEntryAsRecord(e, {
              tenant_id,
              project_ref,
              skill_version,
            }),
          ),
    [logsRead.items, project_ref, tenant_id, skill_version, discardedLogIds],
  );
  // Presentation reconciliation intentionally suppresses a server row when this
  // device retains the same canonical. Mutation authority must not: the server
  // may have folded a newer correction than the retained local baseline.
  const authoritativeServerLogRows = useMemo(
    () =>
      project_ref === null
        ? []
        : logsRead.authoritativeItems.filter((e) => !discardedLogIds.has(e.id)).map((e) =>
            serverLogEntryAsRecord(e, {
              tenant_id,
              project_ref,
              skill_version,
            }),
          ),
    [logsRead.authoritativeItems, project_ref, tenant_id, skill_version, discardedLogIds],
  );
  // The highest change_ordinal the SERVER reports for each entry. A correction
  // must allocate ABOVE it: the device's own cursor is only the true high-water
  // mark while the capturing device is the only one that ever corrects the
  // entry, and a restored or second device starts that cursor at zero.
  const serverOrdinalOf = useMemo(() => {
    const byId = new Map<string, number>();
    for (const e of logsRead.authoritativeItems) byId.set(e.id, e.change_ordinal);
    return (capture_id: string) => byId.get(capture_id) ?? 0;
  }, [logsRead.authoritativeItems]);
  // With no endpoint baked in AND nothing cached, fall back to the dev
  // fixtures so a local `vite dev` still has something to click. A real field
  // build always has an endpoint, so this never fires there — and it is
  // deliberately NOT used to paper over a failed read, which must show the
  // last-known list and say it is stale rather than silently swap in fiction.
  const liveDecisions = readClient === null && decisionsRead.source === "none"
    ? SEED_DECISIONS
    : decisionsRead.items;
  const liveActions = readClient === null && actionsRead.source === "none"
    ? SEED_MASTER_ITEMS
    : actionsRead.items;

  return {
    logsRead,
    projectRefused,
    unseenRejections,
    dismissRejection,
    projectLocalRecords,
    serverOnlyLogRows,
    authoritativeServerLogRows,
    serverOrdinalOf,
    liveDecisions,
    liveActions,
  };
}
